import { Component, computed, Injector, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Agent, AgentCmmunicationTypeEnum, AgentTypeEnum } from '@trendbuild/trend-gpt-maker-api';
import { AbstractComponent, AgentTrendGPTService, Channel, ChannelService, Department, Integration, IntegrationService } from 'lib-trend-core';
import { finalize, firstValueFrom, takeUntil } from 'rxjs';
import { AgentConfigsFormComponent } from '../agent-configs-form/agent-configs-form.component';

type Step = { title: string; subtitle: string };
export type ChannelRef = { _id: string; name: string; type: string };

@Component({
    selector: 'app-agent-trendgpt-form',
    templateUrl: './agent-trendgpt-form.component.html',
    styleUrls: ['./agent-trendgpt-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgentTrendgptFormComponent extends AbstractComponent {
  @ViewChild(AgentConfigsFormComponent) configsForm!: AgentConfigsFormComponent;

  public agent: Agent;
  public integration: Integration;
  public currentAgentId: string;
  public override loading: boolean = false;

  public listChannel: Channel[] = [];
  public channelRef: ChannelRef[] = [];
  public departments: Department[] = [];

  public agentTypes = [
    { label: 'agents.trendgptForm.typeSupportLabel', value: AgentTypeEnum.SUPPORT, subtitle: 'agents.trendgptForm.typeSupportSubtitle', icon: 'mdi mdi-account-outline' },
    { label: 'agents.trendgptForm.typeSaleLabel', value: AgentTypeEnum.SALE, subtitle: 'agents.trendgptForm.typeSaleSubtitle', icon: 'mdi mdi-cart-outline' },
    { label: 'agents.trendgptForm.typePersonalLabel', value: AgentTypeEnum.PERSONAL, subtitle: 'agents.trendgptForm.typePersonalSubtitle', icon: 'mdi mdi-account' },
  ]
  public comunicationTypes = [
    { label: 'agents.trendgptForm.commNormalLabel', value: AgentCmmunicationTypeEnum.NORMAL, subtitle: 'agents.trendgptForm.commNormalSubtitle', icon: 'mdi mdi-chat-outline' },
    { label: 'agents.trendgptForm.commRelaxedLabel', value: AgentCmmunicationTypeEnum.RELAXED, subtitle: 'agents.trendgptForm.commRelaxedSubtitle', icon: 'mdi mdi-heart-outline' },
    { label: 'agents.trendgptForm.commFormalLabel', value: AgentCmmunicationTypeEnum.FORMAL, subtitle: 'agents.trendgptForm.commFormalSubtitle', icon: 'mdi mdi-briefcase-outline' },
  ]

  stepIndex = signal(0);

  steps: Step[] = [
    { title: 'agents.trendgptForm.stepBasicTitle', subtitle: 'agents.trendgptForm.stepBasicSubtitle' },
    { title: 'agents.trendgptForm.stepPersonalityTitle', subtitle: 'agents.trendgptForm.stepPersonalitySubtitle' },
    { title: 'agents.trendgptForm.stepTrainingTitle', subtitle: 'agents.trendgptForm.stepTrainingSubtitle' },
    { title: 'agents.trendgptForm.stepIntentionsTitle', subtitle: 'agents.trendgptForm.stepIntentionsSubtitle' },
    { title: 'agents.trendgptForm.stepConfigTitle', subtitle: 'agents.trendgptForm.stepConfigSubtitle' },
  ];

  breadcrumb = [
    { href: '/agents/trendgpt', label: 'agents.trendgptForm.breadcrumbAgents' },
    { label: 'agents.trendgptForm.breadcrumbEdit' },
  ]

  constructor(
    injector: Injector,
    private integrationService: IntegrationService,
    private channelService: ChannelService,
    private agentTrendGPTService: AgentTrendGPTService,
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
    this.currentAgentId = this.getParam('id');
    this.getIntegrationTrendGPTByCompany();
    this.getListChannel();

    if (!this.currentAgentId) return;

    this.loadingContent = true;
    firstValueFrom(this.agentTrendGPTService.getAgentById(this.currentAgentId))
      .then(agent => {
        this.agent = agent as Agent;
        this.formGroup.patchValue({
          name: agent.name ?? null,
          channels: this.channelRef.map(c => c._id) ?? [],
          communicationType: agent.communicationType ?? null,
          type: agent.type ?? null,
          behavior: agent.behavior ?? '',
          jobSite: agent.jobSite ?? null,
          jobName: agent.jobName ?? null,
          jobDescription: agent.jobDescription ?? null,
        }, { emitEvent: false });

      })
      .catch(() => this.alertService.error(this.t('agents.trendgptForm.loadAgentError')))
      .finally(() => this.loadingContent = false);
  }

  setupForm(): void {
    this.formGroup = this.formBuilder.group({
      name: [null],
      channels: [[]],
      communicationType: [null],
      type: [null],
      behavior: '',
      jobSite: [null],
      greetingMessage: [null],
      jobName: [null],
      jobDescription: [null],
    });
  }

  get selectedAgentType(): AgentTypeEnum | null {
    return this.formGroup.get('type')?.value ?? null;
  }

  get selectedCommunicationType(): AgentCmmunicationTypeEnum | null {
    return this.formGroup.get('communicationType')?.value ?? null;
  }

  handleSelectAgentType(type: AgentTypeEnum): void {
    this.formGroup.get('type')?.setValue(type);
  }

  handleSelectCommunicationType(type: AgentCmmunicationTypeEnum): void {
    this.formGroup.get('communicationType')?.setValue(type);
  }

  isSelectedType(type: AgentTypeEnum) {
    return this.selectedAgentType === type;
  }

  isSelectedCommunicationType(type: AgentCmmunicationTypeEnum) {
    return this.selectedCommunicationType === type;
  }

  nextStep(): void {
    const maxIndex = Math.max(this.steps.length - 1, 0);
    this.stepIndex.update(index => Math.min(index + 1, maxIndex));
  }

  async saveConfigs(): Promise<void> {
    if (this.configsForm) {
      this.loading = true;
      try {
        await this.configsForm.save();
      } finally {
        this.loading = false;
      }
    }
  }

  async saveAndNextStep(): Promise<void> {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const payload = this.formGroup.getRawValue() as Agent;
    delete payload.avatar

    const selectedChannelIds = this.formGroup.get('channels')?.value || [];
    const selectedChannels = selectedChannelIds.map((idChannel: string) => {
      const channel = this.listChannel.find(c => c._id === idChannel);
      return { _id: channel._id, name: channel.name, type: channel.type };
    });

    try {
      const saved = await firstValueFrom(this.agentTrendGPTService.updateAgentById(this.currentAgentId, payload)) as Agent;
      this.agent = saved;
      this.currentAgentId = saved.id ?? this.currentAgentId;

      await this.setupChannelsAssistant(this.currentAgentId, selectedChannels, this.agent.name ?? '');
      this.alertService.success(this.t('agents.trendgptForm.saveAgentSuccess'));

      const max = Math.max(this.steps.length - 1, 0);
      if (this.clampedIndex() < max) this.nextStep();
    } catch {
      this.alertService.error(this.t('agents.trendgptForm.saveAgentError'));
    } finally {
    }
  }

  previousStep(): void {
    this.stepIndex.update(index => Math.max(index - 1, 0));
  }

  get currentStep(): number {
    return this.currentStepSignal();
  }

  get progressPct(): number {
    return this.progressSignal();
  }

  get counter(): string {
    return this.t('agents.trendgptForm.stepCounter', { current: this.currentStep, total: this.steps.length });
  }

  private clampedIndex = computed(() => {
    const total = this.steps.length;
    return total ? Math.min(Math.max(this.stepIndex(), 0), total - 1) : 0;
  });

  private currentStepSignal = computed(() => this.clampedIndex() + 1);
  private progressSignal = computed(() => {
    const total = this.steps.length;
    return total > 1 ? (this.clampedIndex() / (total - 1)) * 100 : 0;
  });

  getIntegrationTrendGPTByCompany() {
    this.loadingContent = true;
    this.integrationService.getIntegrationTrendGPTByCompany()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.loadingContent = false; })
      )
      .subscribe({
        next: (integration: Integration) => {
          this.integration = integration;
          this.channelRef = this.integration.metadata?.['agents']?.find((a: any) => a.id === this.currentAgentId)?.channels ?? [];
        },
        error: (err) => {
          this.alertService.error(err.error.message);
        }
      });
  }

  setupChannelsAssistant(agentId: string, channels: Channel[], agentName: string) {
    const metadata = this.integration.metadata || {};
    let agents = metadata['agents'] || [];

    const index = agents.findIndex((a) => a.id === agentId);

    if (index >= 0) {
      agents[index] = { name: agentName, id: agentId, createdAt: new Date(), channels };
    } else {
      agents.push({ name: agentName, id: agentId, createdAt: new Date(), channels });
    }

    metadata['agents'] = agents;

    const updatedIntegration = { ...this.integration, metadata };

    return new Promise<void>((resolve, reject) => {
      this.integrationService.update(this.integration._id, updatedIntegration)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => resolve(),
          error: (err) => {
            this.alertService.error(err.error.message);
            reject(err);
          },
        });
    });
  }

  private getListChannel(): void {
    this.channelService.getList().subscribe({
      next: (list: Array<Channel>) => {
        this.listChannel = list;
      },
      error: (err) => {
        console.error('Erro ao carregar canais:', err);
        this.alertService.error(this.t('agents.trendgptForm.loadChannelsError'));
      }
    });
  }

}
